import { useMemo, useState, type JSX } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Phase 24 (v1.4) — BulkScheduleStep (restructured 2026-05-14)
 *
 * Step 3 of the bulk-create wizard. Captures the batch-level entry date and
 * shift only — start time / end time inputs moved to step 4 (BulkMachineStep
 * "Timing" panel) so step 3 is one focused decision: date + shift.
 *
 * Shift is rendered as three buttons: Morning / Afternoon / Night.
 *
 * NOTE: single-create's LogsheetEntryForm offers a fourth "Custom" option for
 * off-shift entries. Bulk deliberately omits Custom — bulk batches are
 * shift-aligned by definition. If a maker needs Custom they should use the
 * single-create wizard. Keep these sets in sync only on the three named
 * shifts so reports filtered by shift aggregate cleanly across flows.
 *
 * Validation (Q1–Q14 locked decisions):
 *  - entryDate: required, must not be in the future
 *  - shift: optional; one of Morning/Afternoon/Night when set
 *  - startTime/endTime: not edited here; the parent merges step-3 output
 *    (entryDate + shift) with its existing schedule so timing is preserved
 *    and gated by step 4
 */

const SHIFT_OPTIONS = ['Morning', 'Afternoon', 'Night'] as const;
type ShiftOption = (typeof SHIFT_OPTIONS)[number];

export interface BulkScheduleValues {
  /** ISO date YYYY-MM-DD. */
  readonly entryDate: string;
  readonly shift: string | null;
  /** HH:mm. Edited in step 4. */
  readonly startTime: string;
  /** HH:mm, optional. Edited in step 4. */
  readonly endTime: string | null;
}

/** What step 3 actually owns — narrower than `BulkScheduleValues` so the type
 *  system reflects that startTime/endTime are no longer step-3 concerns. */
export type BulkScheduleStepValues = Pick<BulkScheduleValues, 'entryDate' | 'shift'>;

export interface BulkScheduleStepProps {
  readonly initial: BulkScheduleValues;
  readonly onSubmit: (values: BulkScheduleStepValues) => void;
  readonly onBack: () => void;
}

interface FieldErrors {
  entryDate?: string;
  shift?: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function makeSchema(today: string): z.ZodType<Pick<BulkScheduleValues, 'entryDate' | 'shift'>> {
  return z.object({
    entryDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/u, 'Entry date is required (YYYY-MM-DD).')
      .refine((d) => d <= today, { message: 'Entry date must not be in the future.' }),
    shift: z
      .string()
      .max(50, 'Shift must be 50 characters or fewer.')
      .nullable(),
  });
}

export function BulkScheduleStep({
  initial,
  onSubmit,
  onBack,
}: BulkScheduleStepProps): JSX.Element {
  const today = useMemo(todayIso, []);
  const [entryDate, setEntryDate] = useState<string>(initial.entryDate);
  const [shift, setShift] = useState<string | null>(initial.shift);
  const [errors, setErrors] = useState<FieldErrors>({});

  function handleNext(): void {
    const candidate = { entryDate, shift };
    const schema = makeSchema(today);
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        switch (key) {
          case 'entryDate':
            if (next.entryDate === undefined) next.entryDate = issue.message;
            break;
          case 'shift':
            if (next.shift === undefined) next.shift = issue.message;
            break;
          default:
            // A schema rule fired but maps to no UI surface. Loud in dev so a
            // future maintainer who adds a refine doesn't lose its error.
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.warn(
                `[BulkScheduleStep] Unmapped zod issue at path ${JSON.stringify(issue.path)}: ${issue.message}`,
              );
            }
        }
      }
      setErrors(next);
      return;
    }
    setErrors({});
    // Step 3 owns only entryDate + shift. Parent merges this with the
    // existing schedule so timing (startTime / endTime) is preserved and
    // remains the responsibility of step 4.
    onSubmit({
      entryDate: parsed.data.entryDate,
      shift: parsed.data.shift,
    });
  }

  return (
    <div className="space-y-6" data-testid="bulk-step-schedule">
      {/* Date */}
      <div className="space-y-1">
        <Label htmlFor="bulk-schedule-entry-date">Entry date *</Label>
        <Input
          id="bulk-schedule-entry-date"
          type="date"
          max={today}
          value={entryDate}
          onChange={(e) => {
            setEntryDate(e.target.value);
          }}
          data-testid="bulk-schedule-entry-date"
          aria-invalid={errors.entryDate !== undefined}
        />
        {errors.entryDate !== undefined && (
          <p className="text-xs text-destructive" data-testid="bulk-schedule-error-entryDate">
            {errors.entryDate}
          </p>
        )}
      </div>

      {/* Shift — three toggle buttons. We use `role="group"` + `aria-pressed`
          (toggle-button pattern) rather than `role="radiogroup"` because the
          buttons don't implement WAI-ARIA arrow-key navigation. Each button
          is reachable via Tab; the visual contract (one selected) is
          communicated via aria-pressed. */}
      <div className="space-y-2">
        <Label>Shift</Label>
        <div
          className="grid grid-cols-3 gap-2"
          role="group"
          aria-label="Shift"
          data-testid="bulk-schedule-shift-group"
        >
          {SHIFT_OPTIONS.map((opt: ShiftOption) => {
            const active = shift === opt;
            return (
              <Button
                key={opt}
                type="button"
                variant={active ? 'default' : 'outline'}
                onClick={() => {
                  setShift(opt);
                }}
                aria-pressed={active}
                data-testid={`bulk-schedule-shift-${opt.toLowerCase()}`}
                className="min-h-11"
              >
                {opt}
              </Button>
            );
          })}
        </div>
        {errors.shift !== undefined && (
          <p className="text-xs text-destructive" data-testid="bulk-schedule-error-shift">
            {errors.shift}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          data-testid="bulk-schedule-back"
        >
          Back
        </Button>
        <Button type="button" onClick={handleNext} data-testid="bulk-schedule-next">
          Next
        </Button>
      </div>
    </div>
  );
}
